import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { Box, Chip, CircularProgress, IconButton, Typography } from '@mui/material';
import React from 'react';
import type { VideoAsset } from '../../../services/admin/adminBackendServiceVideoLibraryMixin';
import AssetImage from '../../AssetImage';
import { useVideoLibraryCardInteraction } from './useVideoLibraryCardInteraction';

function formatDuration(seconds?: number): string {
  if (!seconds) return '';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60)
    .toString()
    .padStart(2, '0');
  return `${mins}:${secs}`;
}

function StatusBadge({ video }: { video: VideoAsset }) {
  if (video.status === 'PENDING' || video.status === 'PROCESSING') {
    return (
      <Chip
        size="small"
        icon={<CircularProgress size={12} sx={{ color: 'inherit' }} />}
        label="Compressing…"
        color="info"
      />
    );
  }
  if (video.status === 'FAILED') {
    return <Chip size="small" icon={<ErrorIcon />} label="Processing failed" color="error" />;
  }
  return <Chip size="small" label={formatDuration(video.durationSeconds)} variant="outlined" />;
}

const ACTION_SX = {
  bgcolor: 'rgba(255,255,255,0.9)',
  '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
};

function VideoLibraryThumbnail({
  video,
  onDelete,
  onEdit,
}: {
  video: VideoAsset;
  onDelete?: () => void;
  onEdit?: () => void;
}) {
  const isBusy = video.status === 'PENDING' || video.status === 'PROCESSING';

  return (
    <Box
      sx={{
        position: 'relative',
        height: 124,
        bgcolor: '#f1f4f8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {isBusy ? (
        <CircularProgress size={24} />
      ) : video.posterUrl ? (
        <AssetImage
          url={video.posterUrl}
          alt={video.title}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <ErrorIcon sx={{ color: 'text.disabled' }} />
      )}
      {(onEdit || onDelete) && (
        <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5 }}>
          {onEdit && (
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onEdit();
              }}
              aria-label={`Edit ${video.title}`}
              sx={ACTION_SX}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {onDelete && (
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onDelete();
              }}
              aria-label={`Delete ${video.title}`}
              sx={ACTION_SX}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
}

export default function VideoLibraryCard({
  video,
  onDelete,
  onEdit,
  onPick,
  fluid = false,
  onDragStart,
  onDragEnd,
}: {
  video: VideoAsset;
  onDelete?: () => void;
  onEdit?: () => void;
  onPick?: () => void;
  /** Fill the container's width instead of the fixed grid-card width. */
  fluid?: boolean;
  /** When set, the card can be dragged onto a slide (carries the video's externalId). */
  onDragStart?: () => void;
  onDragEnd?: () => void;
}) {
  const interaction = useVideoLibraryCardInteraction({ video, onPick, onDragStart, onDragEnd });
  return (
    <Box
      draggable={interaction.draggable}
      onDragStart={interaction.onDragStart}
      onDragEnd={interaction.onDragEnd}
      role={interaction.role}
      aria-label={interaction.ariaLabel}
      tabIndex={interaction.tabIndex}
      onClick={interaction.onClick}
      onKeyDown={interaction.onKeyDown}
      sx={{
        width: fluid ? '100%' : 220,
        flexShrink: 0,
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        cursor: interaction.cursor,
      }}
    >
      <VideoLibraryThumbnail video={video} onDelete={onDelete} onEdit={onEdit} />
      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Typography variant="body2" fontWeight={600} noWrap title={video.title}>
          {video.title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <StatusBadge video={video} />
          {video.status === 'READY' && (
            <Typography variant="caption" color="text.secondary">
              {video.referenceCount ? `Used in ${video.referenceCount}` : 'Not used yet'}
            </Typography>
          )}
        </Box>
        {video.status === 'FAILED' && video.errorMessage && (
          <Typography variant="caption" color="error.main">
            {video.errorMessage}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
